// TemplatesSection.js
// ─────────────────────────────────────────────────────────────────────────────
// CLIENT DOCUMENT TEMPLATES
//
// A separate library of clients' document templates (HTML format). Each template
// is tied to a document type (site instruction, …). The captured register data
// is later merged into the chosen template from the record's "Populate template"
// button (see PopulateTemplateModal); blank fields print as "n/a".
//
// Firestore: top-level `templates/{id}` — { name, client, docType, html }.
// Admin-managed (controlled documents); any signed-in member may read/use them.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";
import { renderTemplate, extractTokens, FIELD_VOCAB, templateScope, sanitizeTemplateHtml } from "../utils/templateEngine";
import { printDocHtml } from "../utils/printDoc";
import { extractUploadedDoc, aiTokenizeTemplate } from "../utils/aiTemplate";
import { STARTERS } from "./templateStarters";

const C = tokens.color;
const MONO = tokens.font?.mono || '"JetBrains Mono", monospace';

const DOC_TYPES = [
  { value: "instruction", label: "Site Instruction" },
  { value: "variation", label: "Variation Order" },
  { value: "certificate", label: "Payment Certificate" },
  { value: "rfi", label: "RFI" },
  { value: "other", label: "Other" },
];

// Neutral placeholder sample so the editor preview shows layout without leaking
// any real client's project data.
const SAMPLE = {
  siNumber: "SI-001", projectNumber: "0000", clientProjectNumber: "0000/WP",
  contractNumber: "C-0000/0000",
  contractDescription: "Sample contract description",
  contractor: "Sample Contractor", subject: "Sample subject",
  instruction: "Sample instruction text.",
  applicablePayItems: "", drawingRevisionRef: "DWG-000 Rev 0",
  asBuiltImplications: "No", financialImplications: "No", valuationBasis: "None",
  employersAgentRepresentative: "Engineer", instructionDate: "2026-01-01", instructionTime: "",
  contractorRemarks: "", receivedBy: "", receivedDate: "", receivedTime: "", distribution: "Site file",
};

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 12px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12.5, color: C.ink, fontFamily: "inherit", outline: "none" },
  mono: { fontFamily: MONO, fontSize: 12, lineHeight: 1.5 },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnSmall: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "8px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  tag: { display: "inline-block", padding: "2px 8px", margin: "0 4px 4px 0", borderRadius: 6, fontSize: 11, fontFamily: MONO, background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.ink, cursor: "pointer" },
  iframe: { width: "100%", height: 420, border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff" },
};

const blank = () => ({ name: "", client: "", docType: "instruction", html: "" });

function TemplatesSection({ currentProject }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);

  // Templates are isolated per client — this project's client only.
  const scope = templateScope(currentProject);
  const clientLabel = currentProject?.client || currentProject?.clientId || currentProject?.id || "";

  useEffect(() => {
    if (!scope) { setTemplates([]); return undefined; }
    const q = query(collection(db, "templates"), where("scope", "==", scope));
    const unsub = onSnapshot(q,
      (snap) => setTemplates(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
      ),
      (err) => console.error("templates listener:", err)
    );
    return unsub;
  }, [scope]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const usedTokens = useMemo(() => extractTokens(form.html), [form.html]);
  const vocab = FIELD_VOCAB[form.docType] || [];
  const preview = useMemo(() => renderTemplate(form.html, SAMPLE), [form.html]);

  const filtered = filterType ? templates.filter((t) => t.docType === filterType) : templates;

  function reset() { setForm(blank()); setEditingId(null); }

  async function save() {
    if (!scope) { alert("Select a project first — templates are stored per client."); return; }
    if (!form.name.trim()) { alert("Template name is required."); return; }
    if (!form.html.trim()) { alert("Template body is empty."); return; }
    const safeHtml = sanitizeTemplateHtml(form.html);
    if (!safeHtml.trim()) { alert("Template body has no safe printable content after sanitizing."); return; }
    const payload = {
      name: form.name.trim(), client: form.client.trim() || clientLabel,
      docType: form.docType, html: safeHtml, scope, updatedAt: serverTimestamp(),
    };
    try {
      if (editingId) await updateDoc(doc(db, "templates", editingId), payload);
      else await addDoc(collection(db, "templates"), { ...payload, createdAt: serverTimestamp() });
      reset();
    } catch (e) { alert(`Save failed: ${e.message || e}\n(Templates are admin-managed — check your role.)`); }
  }

  // Upload the client's actual document and let Claude insert the merge tokens.
  async function handleUploadAuto(e) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setAiBusy(true);
    try {
      const raw = await extractUploadedDoc(file);
      const html = sanitizeTemplateHtml(await aiTokenizeTemplate(raw, form.docType, FIELD_VOCAB[form.docType] || []));
      if (!html) throw new Error("The model returned an empty template.");
      setForm((f) => ({
        ...f,
        name: f.name || file.name.replace(/\.[^.]+$/, ""),
        client: f.client || clientLabel,
        html,
      }));
      alert("Template auto-mapped. Review the tokens in the preview, adjust if needed, then Save.");
    } catch (err) {
      alert(`Auto-map failed: ${err.message || err}`);
    } finally {
      setAiBusy(false);
    }
  }

  function edit(t) {
    setForm({ name: t.name || "", client: t.client || "", docType: t.docType || "instruction", html: t.html || "" });
    setEditingId(t.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function remove(id) {
    if (!window.confirm("Delete this template?")) return;
    try { await deleteDoc(doc(db, "templates", id)); }
    catch (e) { alert(`Delete failed: ${e.message || e}`); }
  }

  function insertStarter(key) {
    const st = STARTERS.find((x) => x.key === key);
    if (!st) return;
    setForm((f) => ({
      ...f,
      name: f.name || st.name,
      client: f.client || clientLabel,
      docType: st.docType,
      html: st.html,
    }));
  }

  const insertToken = (tok, kind) => {
    const snippet = kind === "check" ? `{{check:${tok}=VALUE}}` : kind === "date" ? `{{date:${tok}}}` : `{{${tok}}}`;
    setForm((f) => ({ ...f, html: (f.html || "") + snippet }));
  };

  if (!scope) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <h1 style={s.title}>Client Document Templates</h1>
          <p style={s.sub}>Select a project first. Templates are stored per client and are only visible on that client's projects.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1 style={s.title}>Client Document Templates</h1>
            <p style={s.sub}>
              Store this client's letterhead formats. From a captured record use “Populate template” to merge the
              data in and generate a PDF — blank fields print as “n/a”.
            </p>
          </div>
          <div style={{ ...s.statTile, alignSelf: "center" }}>
            <p style={s.statLabel}>Client scope</p>
            <p style={{ ...s.statValue, fontSize: 14 }}>{clientLabel || "—"}</p>
          </div>
        </div>
      </div>

      {/* ── editor ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <h3 style={{ ...s.h3, margin: 0 }}>{editingId ? "Edit template" : "New template"}</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input ref={fileRef} type="file" accept=".docx,.html,.htm,.txt" style={{ display: "none" }} onChange={handleUploadAuto} />
            <button style={s.btnAccent} disabled={aiBusy} onClick={() => fileRef.current && fileRef.current.click()}>
              {aiBusy ? "Mapping…" : "⬆ Upload & auto-map (AI)"}
            </button>
            {STARTERS.map((st) => (
              <button key={st.key} style={s.btnSmall} onClick={() => insertStarter(st.key)}>⤓ Insert “{st.name}”</button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <div><label style={s.lbl}>Template name</label><input style={s.inp} value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Site Instruction" /></div>
          <div><label style={s.lbl}>Client</label><input style={s.inp} value={form.client} onChange={(e) => setField("client", e.target.value)} placeholder={clientLabel || "Client name"} /></div>
          <div><label style={s.lbl}>Document type</label>
            <select style={s.inp} value={form.docType} onChange={(e) => setField("docType", e.target.value)}>
              {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 16, marginTop: 14, alignItems: "start" }}>
          <div>
            <label style={s.lbl}>Template HTML (use {"{{tokens}}"})</label>
            <textarea style={{ ...s.inp, ...s.mono, minHeight: 360, resize: "vertical" }} value={form.html} onChange={(e) => setField("html", e.target.value)} placeholder="Paste the client's form as HTML, then drop in tokens…" />
            <div style={{ marginTop: 8 }}>
              <span style={s.lbl}>Available fields for “{DOC_TYPES.find((d) => d.value === form.docType)?.label}” — click to insert</span>
              <div>
                {vocab.length === 0 && <span style={s.sub}>Free-form (use any token; data is merged by field name).</span>}
                {vocab.map((tok) => (
                  <span key={tok} style={s.tag} title="insert {{token}}" onClick={() => insertToken(tok, "text")}>{`{{${tok}}}`}</span>
                ))}
              </div>
              <p style={{ ...s.sub, marginTop: 6 }}>
                Checkbox: <code>{"{{check:valuationBasis=New Rates}}"}</code> · Date: <code>{"{{date:instructionDate}}"}</code>
              </p>
            </div>
          </div>

          <div>
            <label style={s.lbl}>Live preview (sample data)</label>
            <iframe title="template-preview" style={s.iframe} sandbox="" srcDoc={preview} />
            {usedTokens.length > 0 && (
              <p style={{ ...s.sub, marginTop: 6 }}>Tokens used: {usedTokens.join(", ")}</p>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button style={s.btnAccent} onClick={save}>{editingId ? "Update template" : "Save template"}</button>
          {editingId && <button style={s.btn} onClick={reset}>Cancel</button>}
          <button style={s.btn} disabled={!form.html.trim()} onClick={() => printDocHtml(preview, form.name || "template-preview")}>Preview PDF (sample)</button>
        </div>
      </div>

      {/* ── library ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <h3 style={{ ...s.h3, margin: 0 }}>Template library</h3>
          <select style={{ ...s.inp, width: 200 }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All document types</option>
            {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Name", "Client", "Type", "Tokens", "Actions"].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id}>
                  <td style={{ ...s.td, fontWeight: 700 }}>{t.name}</td>
                  <td style={s.td}>{t.client || "—"}</td>
                  <td style={s.td}>{DOC_TYPES.find((d) => d.value === t.docType)?.label || t.docType}</td>
                  <td style={{ ...s.td, ...s.mono }}>{extractTokens(t.html).length}</td>
                  <td style={s.td}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button style={s.btnSmall} onClick={() => edit(t)}>Edit</button>
                      <button style={s.btnDanger} onClick={() => remove(t.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td style={{ ...s.td, color: C.inkMuted }} colSpan={5}>No templates yet. Use “Insert” above to start from the bundled generic Site Instruction, or “Upload & auto-map (AI)”.</td></tr>}
            </tbody>
          </table>
        </div>
        <p style={s.sub}>These templates belong to {clientLabel || "this client"} and are not visible on other clients' projects.</p>
      </div>
    </div>
  );
}

export default React.memo(TemplatesSection);
